// Prepare page - Crear reunión con contexto completo

import Alpine from 'alpinejs';
import Swal from 'sweetalert2';
import { v4 as uuid } from 'uuid';
import { getDB } from '../storage/db.js';
import { MeetingManager } from '../storage/meetingManager.js';
import { Outbox } from '../storage/outbox.js';

Alpine.data('preparePage', () => ({
  meeting: {
    id: '',
    title: '',
    objective: '',
    client_name: '',
    client_context: '',
    consulting_tags: [] as string[],
    glossary: {} as Record<string, string>,
    status: 'draft',
    scheduled_at: '',
    owner_user_id: '',
    created_at: '',
    updated_at: '',
  },
  participants: [] as Array<{ id: string; name: string; role: string; type: string; email: string; seq: number }>,
  topics: [] as Array<{ id: string; seq: number; label: string; goal: string; expected_outcome: string }>,
  meetingManager: null as any,

  async init() {
    this.meeting.id = uuid();
    this.meeting.owner_user_id = 'local-user'; // TODO: Get from auth
    this.meeting.created_at = new Date().toISOString();
    this.meeting.updated_at = new Date().toISOString();
    
    // Initialize MeetingManager
    const db = await getDB();
    const outbox = new Outbox(db);
    this.meetingManager = new MeetingManager(db, outbox);
  },

  addParticipant() {
    this.participants.push({
      id: uuid(),
      name: '',
      role: '',
      type: 'client',
      email: '',
      seq: this.participants.length,
    });
  },

  removeParticipant(index: number) {
    this.participants.splice(index, 1);
    // Reorder seq
    this.participants.forEach((p: any, i: number) => p.seq = i);
  },

  addTopic() {
    this.topics.push({
      id: uuid(),
      seq: this.topics.length,
      label: '',
      goal: '',
      expected_outcome: '',
    });
  },

  removeTopic(index: number) {
    this.topics.splice(index, 1);
    this.topics.forEach((t: any, i: number) => t.seq = i);
  },

  async saveDraft() {
    if (!this.meeting.title) {
      Swal.fire('Error', 'El título es obligatorio', 'error');
      return;
    }

    try {
      // Update timestamp
      this.meeting.updated_at = new Date().toISOString();
      
      // Save to IndexedDB directly (MeetingManager methods need adjustment)
      const db = await getDB();
      
      // Clean meeting data using JSON serialization (ensures only serializable data)
      const meetingData = JSON.parse(JSON.stringify({
        id: this.meeting.id,
        title: this.meeting.title,
        objective: this.meeting.objective,
        client_name: this.meeting.client_name,
        client_context: this.meeting.client_context,
        consulting_tags: [...(this.meeting.consulting_tags || [])],
        glossary: {...(this.meeting.glossary || {})},
        status: this.meeting.status,
        scheduled_at: this.meeting.scheduled_at,
        owner_user_id: this.meeting.owner_user_id,
        created_at: this.meeting.created_at,
        updated_at: this.meeting.updated_at,
      }));

      // Save meeting
      const meetingTx = db.transaction('meetings', 'readwrite');
      await meetingTx.objectStore('meetings').put(meetingData);
      
      // Save participants if any
      if (this.participants.length > 0) {
        const partTx = db.transaction('participants', 'readwrite');
        const partStore = partTx.objectStore('participants');
        for (const participant of this.participants) {
          const participantData = JSON.parse(JSON.stringify({
            id: participant.id,
            meeting_id: this.meeting.id,
            name: participant.name || '',
            role: participant.role || '',
            type: participant.type || 'client',
            email: participant.email || '',
            seq: participant.seq || 0,
          }));
          await partStore.put(participantData);
        }
      }
      
      // Save topics if any
      if (this.topics.length > 0) {
        const topicTx = db.transaction('topics', 'readwrite');
        const topicStore = topicTx.objectStore('topics');
        for (const topic of this.topics) {
          const topicData = JSON.parse(JSON.stringify({
            id: topic.id,
            meeting_id: this.meeting.id,
            seq: topic.seq || 0,
            label: topic.label || '',
            goal: topic.goal || '',
            expected_outcome: topic.expected_outcome || '',
          }));
          await topicStore.put(topicData);
        }
      }

      console.log('✅ Meeting saved:', this.meeting.id);

      Swal.fire('¡Guardado!', 'Reunión guardada como borrador', 'success');
      
      // Navigate to dashboard
      setTimeout(() => {
        window.location.hash = 'dashboard';
      }, 1000);
    } catch (error) {
      console.error('Error saving meeting:', error);
      Swal.fire('Error', 'No se pudo guardar la reunión: ' + (error as Error).message, 'error');
    }
  },
}));
